import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import * as IInputs from '../../../../../interfaces/merchants/products/purchases/inputs';
import {
  clearCustomerError,
  inputPurchasingUserInfo,
} from '../../../../../redux/actions/merchants/products/purchases/inputs';
import { Dispatch } from '../../../../../redux/reducers/merchants/resources/books/inputs';
import scrollToTop from '../../../../hoc/scrollToTop';
import PurchaseSubmitButton from '../common/buttons/PurchaseSubmitButton';
import * as $ from '../helper';
import CustomerFields, {
  createInitialState as customerFieldsInitialState,
  submittable as customerFieldsSubmittable,
} from './CustomerFields';

interface ElementProps {
  schemeType: IPurchases.SchemeType;
  settings: IPurchases.SettingCustomer;
  postable: boolean;
  onSubmit: () => void;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      schemeType: IPurchases.SchemeType,
      productId: string,
      userInfo: IInputs.UserInfo,
    ) => Promise<void>;
    handleChange: (name: string) => void;
  };
}

enum FieldNames {
  CustomerFields = 'customerFields',
}

interface Control {
  submittable?: boolean;
}

export type InputValueType = string | boolean;

interface State {
  [FieldNames.CustomerFields]?: IInputs.CustomerFieldsValueType;
  control: Control;
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

class UserInfo extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.createControl = this.createControl.bind(this);
    this.createInitialState = this.createInitialState.bind(this);

    this.state = this.createInitialState();
  }

  private handleChange(name: string, value: InputValueType) {
    const changedState = $.changeObjectByNestedString(this.state, name, value);
    this.setState({
      ...changedState,
      control: this.createControl(changedState as State),
    });
    this.props.actions.handleChange(name);
  }

  private async handleSubmit() {
    const { schemeType, inputs, actions, onSubmit } = this.props;
    try {
      await actions.handleSubmit(schemeType, inputs.productId!, this.state);
      onSubmit();
    } catch (e) {
      // failed
    }
  }

  private createInitialState(): State {
    const { inputs, settings } = this.props;
    const _state: State = { ...inputs.userInfo, control: {} };
    if (!_state[FieldNames.CustomerFields]) {
      _state[FieldNames.CustomerFields] = customerFieldsInitialState(
        settings.customerFields,
      );
    }
    return { ..._state, control: this.createControl(_state) };
  }

  private createControl(state: State): Control {
    const { settings } = this.props;
    const { control } = state;
    control.submittable = customerFieldsSubmittable(
      settings.customerFields!,
      $.getValueByNestedString(state, FieldNames.CustomerFields),
    );
    return control;
  }

  public render() {
    const { intl, settings, postable } = this.props;
    const {
      control: { submittable },
    } = this.state;
    return (
      <>
        {settings.customerFields && (
          <CustomerFields
            customerFields={settings.customerFields}
            onChange={this.handleChange}
            namePrefix={FieldNames.CustomerFields}
            values={
              $.getValueByNestedString(this.state, FieldNames.CustomerFields) ||
              {}
            }
          />
        )}
        <PurchaseSubmitButton
          type="button"
          onClick={this.handleSubmit}
          value={intl.formatMessage({ id: 'purchases.userInfo.submitButton' })}
          disabled={!submittable || !postable}
          postable={postable}
        >
          {intl.formatMessage({ id: 'purchases.userInfo.submitButton' })}
        </PurchaseSubmitButton>
      </>
    );
  }
}

const mapStateProps = ({
  merchantsProductsPurchasesInputs,
}: {
  merchantsProductsPurchasesInputs: IInputs.State;
}): StateProps => ({ inputs: merchantsProductsPurchasesInputs });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSubmit: (
      schemeType: IPurchases.SchemeType,
      productId: string,
      userInfo: IInputs.UserInfo,
    ) => inputPurchasingUserInfo(schemeType, productId, userInfo)(dispatch),
    handleChange: (name: string) => clearCustomerError(name)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(UserInfo)));
